import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion'
import { SAGE } from '../theme/colors'
import { EASE_OUT, EASE_IN, SPRING } from '../theme/motion'
import { useNavTree, useNavGroups } from './navItems'

const IDLE = 'rgba(195,200,191,0.5)'
const ACTIVE_BG = 'rgba(176,206,174,0.12)'
const DRAWER_W = 260

const labelStyle = { fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase' }

// Same unfold choreography as the desktop rail
const groupVariants = {
  open: { transition: { staggerChildren: 0.04, delayChildren: 0.04 } },
  closed: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
}
const guideVariants = {
  open: { scaleY: 1, transition: { duration: 0.45, ease: EASE_OUT } },
  closed: { scaleY: 0, transition: { duration: 0.15 } },
}
const childVariants = {
  open: { opacity: 1, x: 0, transition: { ...SPRING, opacity: { duration: 0.25 } } },
  closed: { opacity: 0, x: -10, transition: { duration: 0.12 } },
}

export default function MobileSidebar({ open, onClose, activeNav, setActiveNav }) {
  const { openGroups, toggleGroup } = useNavGroups(activeNav)
  const navTree = useNavTree()
  // One source of truth: the backdrop dims exactly as far as the drawer is
  // out — on open, on close, and under the finger while swiping it away.
  const x = useMotionValue(-DRAWER_W)
  const backdropOpacity = useTransform(x, [-DRAWER_W, 0], [0, 1])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleNav = (id) => {
    setActiveNav(id)
    onClose()
  }

  // Close on a third of the width or a decisive flick; otherwise spring back
  const onDragEnd = (_, info) => {
    if (info.offset.x < -DRAWER_W / 3 || info.velocity.x < -500) onClose()
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 200,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              opacity: backdropOpacity,
            }}
          />

          {/* Drawer */}
          <motion.aside
            initial={{ x: -DRAWER_W }}
            animate={{ x: 0, transition: SPRING }}
            exit={{ x: -DRAWER_W, transition: { duration: 0.24, ease: EASE_IN } }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={{ left: 1, right: 0.04 }}
            onDragEnd={onDragEnd}
            style={{
              x,
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              width: DRAWER_W,
              zIndex: 201,
              background: 'rgba(27,28,27,0.97)',
              backdropFilter: 'blur(32px)',
              WebkitBackdropFilter: 'blur(32px)',
              borderRight: '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              flexDirection: 'column',
              paddingTop: 24,
              paddingBottom: 32,
            }}
          >
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.06, duration: 0.4, ease: EASE_OUT }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', marginBottom: 32 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #b0ceae, #8ba889)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ color: '#1d361f', fontSize: '1.1rem', fontVariationSettings: "'FILL' 1" }}
                  >
                    home
                  </span>
                </div>
                <div>
                  <div className="font-headline font-bold text-base" style={{ color: SAGE, letterSpacing: '-0.03em' }}>
                    The Manor
                  </div>
                  <div className="font-label text-[9px] uppercase tracking-widest text-outline">
                    All systems optimal
                  </div>
                </div>
              </div>

              <motion.button
                onClick={onClose}
                aria-label="Menü schließen"
                whileTap={{ scale: 0.85, rotate: 90 }}
                transition={SPRING}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: 'none',
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'rgba(195,200,191,0.5)',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '1.1rem' }}>close</span>
              </motion.button>
            </motion.div>

            {/* Nav — rows cascade in behind the drawer's leading edge */}
            <motion.nav
              layoutScroll
              className="no-scrollbar"
              style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, padding: '0 12px', marginBottom: 16 }}
            >
              {navTree.map((item, index) => {
                const isActive = activeNav === item.id
                const childActive = item.children?.some(c => c.id === activeNav) ?? false
                const unfolded = openGroups.has(item.id)
                const lit = isActive || childActive
                return (
                  <motion.div
                    key={item.id}
                    layout="position"
                    initial={{ opacity: 0, x: -18 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      layout: SPRING,
                      default: { delay: 0.08 + index * 0.022, duration: 0.42, ease: EASE_OUT },
                    }}
                    style={{ position: 'relative' }}
                  >
                    <div style={{ position: 'relative' }}>
                      <motion.button
                        onClick={() => handleNav(item.id)}
                        whileTap={{ scale: 0.97 }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          padding: '13px 16px',
                          borderRadius: 12,
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                          background: isActive ? ACTIVE_BG : 'transparent',
                          color: lit ? SAGE : IDLE,
                          borderLeft: isActive ? `3px solid ${SAGE}` : '3px solid transparent',
                          transition: 'background 0.15s, color 0.15s',
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{
                            fontSize: '1.25rem',
                            flexShrink: 0,
                            fontVariationSettings: lit ? "'FILL' 1" : "'FILL' 0",
                            transition: 'font-variation-settings 0.4s ease',
                          }}
                        >
                          {item.icon}
                        </span>
                        <span className="font-label font-semibold" style={labelStyle}>
                          {item.label}
                        </span>
                      </motion.button>

                      {item.children && (
                        <motion.button
                          onClick={() => toggleGroup(item.id)}
                          whileTap={{ scale: 0.85 }}
                          aria-label={`${item.label} ${unfolded ? 'zuklappen' : 'aufklappen'}`}
                          aria-expanded={unfolded}
                          className="text-outline-dim"
                          style={{
                            position: 'absolute',
                            top: '50%',
                            right: 4,
                            marginTop: -20,
                            width: 40,
                            height: 40,
                            border: 'none',
                            borderRadius: 10,
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <motion.span
                            className="material-symbols-outlined"
                            initial={false}
                            animate={{ rotate: unfolded ? 180 : 0 }}
                            transition={SPRING}
                            style={{ fontSize: '1.2rem' }}
                          >
                            expand_more
                          </motion.span>
                        </motion.button>
                      )}
                    </div>

                    <AnimatePresence initial={false} mode="popLayout">
                      {item.children && unfolded && (
                        <motion.div
                          key="children"
                          variants={groupVariants}
                          initial="closed"
                          animate="open"
                          exit="closed"
                          style={{ position: 'relative', margin: '2px 0 4px 28px' }}
                        >
                          {/* Guide line under the parent icon's centre, drawn top-down */}
                          <motion.span
                            variants={guideVariants}
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 1,
                              background: 'rgba(195,200,191,0.12)',
                              originY: 0,
                            }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {item.children.map((child) => {
                              const isChildActive = activeNav === child.id
                              return (
                                <motion.button
                                  key={child.id}
                                  variants={childVariants}
                                  onClick={() => (child.href ? window.open(child.href, '_blank', 'noopener') : handleNav(child.id))}
                                  whileTap={{ scale: 0.97 }}
                                  className="font-label font-semibold"
                                  style={{
                                    ...labelStyle,
                                    padding: '11px 16px 11px 23px',
                                    borderRadius: '0 12px 12px 0',
                                    border: 'none',
                                    borderLeft: isChildActive ? `2px solid ${SAGE}` : '2px solid transparent',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    background: isChildActive ? ACTIVE_BG : 'transparent',
                                    color: isChildActive ? SAGE : IDLE,
                                    transition: 'background 0.15s, color 0.15s',
                                  }}
                                >
                                  {child.label}
                                  {child.href && <span className="material-symbols-outlined align-middle ml-1" style={{ fontSize: '0.8rem' }}>open_in_new</span>}
                                </motion.button>
                              )
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </motion.nav>

            {/* Emergency */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 + navTree.length * 0.022, duration: 0.42, ease: EASE_OUT }}
              style={{ padding: '0 20px' }}
            >
              <motion.button
                whileTap={{ scale: 0.96 }}
                transition={SPRING}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 12,
                  border: 'none',
                  cursor: 'pointer',
                  background: 'rgba(111,55,34,0.8)',
                  color: '#f1a286',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                }}
                className="font-label"
              >
                Emergency Off
              </motion.button>
            </motion.div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
